(() => {
  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const setProductVariables = (productRoot, config) => {
    if (!productRoot) return;
    productRoot.style.setProperty('--orv-bundle-bg', config.dataset.bundleBg || '#FFFFFF');
    productRoot.style.setProperty('--orv-bundle-border', config.dataset.bundleBorder || '#D9DDE5');
    productRoot.style.setProperty('--orv-bundle-selected', config.dataset.bundleSelected || '#3E2B78');
    productRoot.style.setProperty('--orv-bundle-badge-bg', config.dataset.bundleBadgeBg || '#3E2B78');
    productRoot.style.setProperty('--orv-bundle-badge-text', config.dataset.bundleBadgeText || '#FFFFFF');
    productRoot.style.setProperty('--orv-purchase-trust-text', config.dataset.trustTextColor || '#1F2937');
    productRoot.style.setProperty('--orv-purchase-trust-icon', config.dataset.trustIconColor || '#F97316');
    productRoot.style.setProperty('--orv-social-proof-bg', config.dataset.socialBg || '#3E2B78');
    productRoot.style.setProperty('--orv-social-proof-text', config.dataset.socialText || '#FFFFFF');
  };

  const initCountdown = (config, productRoot) => {
    const priceRow = productRoot?.querySelector('.orv-product-price');
    if (!priceRow) return null;

    let urgency = productRoot.querySelector('.orv-product-urgency');

    if (config.dataset.showCountdown !== 'true') {
      if (urgency?._orvienInterval) clearInterval(urgency._orvienInterval);
      urgency?.remove();
      return null;
    }

    if (!urgency) {
      urgency = document.createElement('div');
      urgency.className = 'orv-product-urgency';
      priceRow.insertAdjacentElement('afterend', urgency);
    }

    if (urgency._orvienInterval) {
      clearInterval(urgency._orvienInterval);
      urgency._orvienInterval = null;
    }

    urgency.removeAttribute('role');
    urgency.removeAttribute('aria-label');
    urgency.innerHTML = '<div class="orv-stock-alert"><span class="orv-stock-alert__dot" aria-hidden="true"></span><span>Hurry up! Only a few left in stock!</span></div>';
    return urgency;
  };

  const initPurchaseExtras = (config, productRoot, timer) => {
    const priceRow = productRoot?.querySelector('.orv-product-price');
    if (!priceRow) return;

    productRoot.querySelector('.orv-product-purchase-extras')?.remove();

    const showTrust = config.dataset.showPurchaseTrust === 'true';
    const showSocial = config.dataset.showSocialProof === 'true';
    if (!showTrust && !showSocial) return;

    const extras = document.createElement('div');
    extras.className = 'orv-product-purchase-extras';

    if (showTrust) {
      const items = [
        [config.dataset.trust1Icon, config.dataset.trust1Text],
        [config.dataset.trust2Icon, config.dataset.trust2Text],
        [config.dataset.trust3Icon, config.dataset.trust3Text]
      ].filter((item) => item[1]);

      if (items.length) {
        const trust = document.createElement('div');
        trust.className = 'orv-purchase-trust';
        trust.innerHTML = items.map(([icon, text]) => `
          <div class="orv-purchase-trust__item">
            <span class="orv-purchase-trust__icon" aria-hidden="true">${escapeHtml(icon || '✓')}</span>
            <span>${escapeHtml(text)}</span>
          </div>`).join('');
        extras.appendChild(trust);
      }
    }

    if (showSocial && config.dataset.socialProofText) {
      const social = document.createElement('div');
      social.className = 'orv-product-social-proof';

      const avatarUrls = [
        config.dataset.avatar1,
        config.dataset.avatar2,
        config.dataset.avatar3,
        config.dataset.avatar4,
        config.dataset.avatar5
      ];

      const avatarHtml = avatarUrls.map((url, index) => {
        if (url) {
          return `<img src="${escapeHtml(url)}" width="30" height="30" loading="lazy" alt="" aria-hidden="true">`;
        }
        const fallback = ['✓', '★', '✓', '★', '✓'][index];
        return `<span class="orv-product-social-proof__placeholder" aria-hidden="true">${fallback}</span>`;
      }).join('');

      social.innerHTML = `
        <span class="orv-product-social-proof__avatars">${avatarHtml}</span>
        <strong class="orv-product-social-proof__text">${escapeHtml(config.dataset.socialProofText)}</strong>`;
      extras.appendChild(social);
    }

    (timer || priceRow).insertAdjacentElement('afterend', extras);
  };

  const enhanceBundleRow = (row, index, config) => {
    if (!row) return;

    row.classList.add('orv-bundle-option--enhanced');
    row.setAttribute('role', 'radio');
    row.setAttribute('tabindex', '0');

    const first = row.children[0];
    if (first) {
      first.textContent = '';
      first.className = 'orv-bundle-radio';
      first.setAttribute('aria-hidden', 'true');
    }

    const copy = row.children[1];
    if (copy) copy.classList.add('orv-bundle-copy');

    let priceWrap = row.querySelector('.orv-bundle-price');
    if (!priceWrap) {
      const total = Array.from(row.children).find((child) => child.tagName === 'STRONG');
      if (total) {
        priceWrap = document.createElement('span');
        priceWrap.className = 'orv-bundle-price';
        total.parentNode.insertBefore(priceWrap, total);
        priceWrap.appendChild(total);
      }
    }

    const compare = config.dataset[`bundle${index}Compare`] || '';
    if (priceWrap) {
      let compareEl = priceWrap.querySelector('.orv-bundle-compare');
      if (compare && !compareEl) {
        compareEl = document.createElement('del');
        compareEl.className = 'orv-bundle-compare';
        priceWrap.appendChild(compareEl);
      }
      if (compareEl) {
        compareEl.textContent = compare;
        compareEl.style.display = compare ? '' : 'none';
      }
    }

    const saving = config.dataset[`bundle${index}Saving`] || '';
    if (copy) {
      let saveEl = copy.querySelector('.orv-bundle-saving');
      if (saving && !saveEl) {
        saveEl = document.createElement('small');
        saveEl.className = 'orv-bundle-saving';
        copy.appendChild(saveEl);
      }
      if (saveEl) {
        saveEl.textContent = saving ? `${config.dataset.bundleSavePrefix || 'You save'} ${saving}` : '';
        saveEl.style.display = saving ? '' : 'none';
      }
    }

    const badgeText = config.dataset[`bundle${index}Badge`] || '';
    let badge = row.querySelector('.orv-bundle-corner-badge');
    if (badgeText && !badge) {
      badge = document.createElement('span');
      badge.className = 'orv-bundle-corner-badge';
      row.appendChild(badge);
    }
    if (badge) {
      badge.textContent = badgeText;
      badge.style.display = badgeText ? '' : 'none';
    }

    const note = copy?.querySelector('small:not(.orv-bundle-saving)');
    if (note) {
      const noteText = note.textContent.trim().toLowerCase();
      const badgeLower = badgeText.trim().toLowerCase();
      note.style.display = noteText && badgeLower.includes(noteText) ? 'none' : '';
    }
  };

  const bindBundleRows = (bundleBox, formWrap) => {
    const qty = formWrap?.querySelector('[data-orv-qty]');
    const rows = bundleBox.querySelectorAll('[data-orv-bundle]');

    const syncState = (selected) => {
      rows.forEach((row) => {
        const active = row === selected;
        row.classList.toggle('is-active', active);
        row.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      if (qty && selected) qty.value = selected.dataset.quantity || 1;
    };

    rows.forEach((row) => {
      if (row.dataset.orvExtrasBound === 'true') return;
      row.dataset.orvExtrasBound = 'true';
      const choose = () => syncState(row);
      row.addEventListener('click', choose);
      row.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          choose();
        }
      });
    });

    const active = bundleBox.querySelector('.orv-bundle-option.is-active') || rows[0];
    if (active) syncState(active);
  };

  const initBundles = (config, productRoot) => {
    const formWrap = productRoot?.querySelector('[data-orv-product-form]');
    const bundleBox = formWrap?.querySelector('.orv-bundle-box');
    if (!bundleBox) return;

    const existingRows = Array.from(bundleBox.querySelectorAll('[data-orv-bundle]'));
    existingRows.slice(0, 3).forEach((row, index) => enhanceBundleRow(row, index + 1, config));

    let row4 = bundleBox.querySelector('[data-orv-bundle][data-quantity="4"]');
    if (config.dataset.showBundle4 === 'true') {
      if (!row4) {
        row4 = document.createElement('div');
        row4.className = 'orv-bundle-option orv-bundle-option--enhanced';
        row4.dataset.orvBundle = '';
        row4.dataset.quantity = '4';
        row4.innerHTML = `
          <span class="orv-bundle-radio" aria-hidden="true"></span>
          <span class="orv-bundle-copy">
            <b>${escapeHtml(config.dataset.bundle4Title || '4 Pieces')}</b>
            <small>${escapeHtml(config.dataset.bundle4Note || 'Best value')}</small>
          </span>
          <span class="orv-bundle-price">
            <strong>${escapeHtml(config.dataset.bundle4Price || '')}</strong>
          </span>`;
        bundleBox.appendChild(row4);
      }
      enhanceBundleRow(row4, 4, config);
    } else if (row4) {
      row4.remove();
    }

    bindBundleRows(bundleBox, formWrap);
  };


  const initReflexVariantCards = (productRoot) => {
    const cardsWrap = productRoot?.querySelector('[data-orv-reflex-variant-cards]');
    const formWrap = productRoot?.querySelector('[data-orv-product-form]');
    const select = formWrap?.querySelector('[data-orv-variant]');
    const gallery = productRoot?.querySelector('[data-orv-gallery]');
    const mainImage = gallery?.querySelector('.orv-product-mainmedia img');
    const thumbsWrap = gallery?.querySelector('.orv-product-thumbs');
    if (!cardsWrap || !select || !gallery || !mainImage || !thumbsWrap) return;

    const cards = Array.from(cardsWrap.querySelectorAll('[data-orv-reflex-card]'));
    const selectedLabel = productRoot.querySelector('[data-orv-reflex-selected]');

    const getImages = (card) => {
      const embedded = Array.from(card.querySelectorAll('[data-orv-reflex-image]'))
        .map((node) => String(node.dataset.orvReflexImage || '').trim())
        .filter(Boolean);
      if (embedded.length) return [...new Set(embedded)];
      const primary = String(card.dataset.cardImage || '').trim();
      const extras = String(card.dataset.extraGallery || '').split('|').map((item) => item.trim()).filter(Boolean);
      return [...new Set([primary, ...extras].filter(Boolean))];
    };

    const setMainImage = (src, alt) => {
      if (!src) return;
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.srcset = '';
      mainImage.setAttribute('src', src);
      if (alt) mainImage.alt = alt;
    };

    const renderGallery = (card) => {
      const images = getImages(card);
      if (!images.length) return;

      thumbsWrap.innerHTML = '';
      thumbsWrap.hidden = false;
      thumbsWrap.style.display = 'grid';
      thumbsWrap.style.visibility = 'visible';

      images.forEach((src, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'orv-product-thumb' + (index === 0 ? ' is-active' : '');
        button.setAttribute('data-orv-thumb', '');
        button.dataset.src = src;
        button.dataset.alt = card.dataset.variantTitle || '';

        const img = document.createElement('img');
        img.src = src;
        img.alt = card.dataset.variantTitle || '';
        img.loading = index === 0 ? 'eager' : 'lazy';
        button.appendChild(img);

        button.addEventListener('click', (event) => {
          event.preventDefault();
          thumbsWrap.querySelectorAll('[data-orv-thumb]').forEach((thumb) => thumb.classList.toggle('is-active', thumb === button));
          setMainImage(src, card.dataset.variantTitle || '');
        });

        thumbsWrap.appendChild(button);
      });

      setMainImage(images[0], card.dataset.variantTitle || '');
      gallery.dataset.orvMediaRevision = String(Number(gallery.dataset.orvMediaRevision || 0) + 1);
    };

    const activateCard = (card, updateSelect = true) => {
      if (!card || card.disabled) return;
      cards.forEach((item) => {
        const active = item === card;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (selectedLabel) selectedLabel.textContent = card.dataset.variantTitle || '';
      if (updateSelect && String(select.value) !== String(card.dataset.variantId || '')) {
        select.value = card.dataset.variantId || '';
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      renderGallery(card);
      [60, 180, 420].forEach((delay) => window.setTimeout(() => {
        if (String(select.value) === String(card.dataset.variantId || '')) renderGallery(card);
      }, delay));
    };

    cards.forEach((card) => {
      if (card.dataset.orvReflexBound === 'true') return;
      card.dataset.orvReflexBound = 'true';
      card.addEventListener('click', () => activateCard(card, true));
    });

    if (select.dataset.orvReflexSelectBound !== 'true') {
      select.dataset.orvReflexSelectBound = 'true';
      select.addEventListener('change', () => {
        const match = cards.find((card) => String(card.dataset.variantId || '') === String(select.value));
        if (match) activateCard(match, false);
      });
    }

    const initial = cards.find((card) => String(card.dataset.variantId || '') === String(select.value)) || cards[0];
    if (initial) activateCard(initial, false);
  };

  const initOrvienProductFixes = () => {
    const config = document.querySelector('.orv-product-fixes-config');
    const productRoot = document.querySelector('.orv-product');
    if (!config || !productRoot) return;

    setProductVariables(productRoot, config);
    const timer = initCountdown(config, productRoot);
    initPurchaseExtras(config, productRoot, timer);
    initBundles(config, productRoot);
    initReflexVariantCards(productRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOrvienProductFixes, { once: true });
  } else {
    initOrvienProductFixes();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && event.target.querySelector && event.target.querySelector('.orv-product-fixes-config')) {
      initOrvienProductFixes();
    }
  });
})();